<script setup lang="ts">
    import { commandGroupKey, commandSelectKey, useCommandContext } from './context';

    const props = withDefaults(
        defineProps<{
            /** What the search matches and what `select` gets. Default: the item's text */
            value?: string;
            /** More words the search finds it by, not shown: synonyms, an old name */
            keywords?: readonly string[];
            /** Shown, but it cannot be selected and the keyboard skips it */
            disabled?: boolean;
        }>(),
        { value: undefined, keywords: () => [], disabled: false },
    );

    const emit = defineEmits<{
        /** A click, or Enter while it is active */
        select: [value: string];
    }>();

    const context = useCommandContext('CommandItem');
    const onSelected = inject(commandSelectKey, undefined);
    const element = useTemplateRef<HTMLElement>('element');
    const id = useId();

    const text = ref('');
    const readText = () => (text.value = element.value?.textContent?.trim() ?? '');
    onMounted(readText);
    onUpdated(readText);

    const entry = {
        id,
        value: computed(() => props.value ?? text.value),
        /* Kept while the words are the same: an inline array is new on every render, and the group's visibility reads it */
        keywords: computed<readonly string[]>((previous) =>
            previous?.length === props.keywords.length && previous.every((word, index) => word === props.keywords[index]) ? previous : props.keywords,
        ),
        disabled: computed(() => props.disabled),
        element,
        group: inject(commandGroupKey, undefined),
    };

    /* Registered on mount, when the element exists for DOM ordering. */
    let unregister: (() => void) | undefined;
    onMounted(() => (unregister = context.register(entry)));
    onBeforeUnmount(() => unregister?.());

    const active = computed(() => context.activeId.value === id);
    const visible = computed(() => context.isVisible(entry));

    function onPointermove() {
        if (!props.disabled && !active.value) context.highlight(id);
    }

    function onClick() {
        if (props.disabled) return;
        emit('select', entry.value.value);
        onSelected?.();
    }
</script>

<template>
    <div
        :id="id"
        ref="element"
        role="option"
        data-slot="command-item"
        class="ui-command-item"
        :hidden="!visible"
        :aria-selected="active"
        :aria-disabled="disabled || undefined"
        :data-active="active || undefined"
        :data-disabled="disabled || undefined"
        :data-value="value"
        @pointermove="onPointermove"
        @click="onClick"
    >
        <slot />
    </div>
</template>

<style scoped>
    @layer components {
        .ui-command-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px 12px;
            border-radius: var(--radius-md);
            font-size: var(--text-sm);
            line-height: var(--text-sm--line-height);
            cursor: pointer;
            user-select: none;
        }

        .ui-command-item[hidden] {
            display: none;
        }

        .ui-command-item[data-active] {
            background-color: var(--color-accent);
            color: var(--color-accent-foreground);
        }

        .ui-command-item[data-disabled] {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .ui-command-item > :deep(svg) {
            flex: none;
            width: 20px;
            height: 20px;
            color: var(--color-muted-foreground);
        }
    }
</style>
